/**
 * Utilities for filtering and searching MCC (Merchant Category Code) business types
 */

import { BusinessTypeCategoryDto } from '../../../../api-dtos/business-types/business-types.dto.interface'
import { BusinessTypeWithCategory } from '../types'

/**
 * Filters business types based on search term with relevance scoring
 */
export const getFilteredBusinessTypes = (
  mccCategories: BusinessTypeCategoryDto[],
  searchTerm: string,
): BusinessTypeWithCategory[] => {
  if (!searchTerm || searchTerm.trim() === '') {
    return mccCategories.flatMap((category) =>
      category.businessTypes.map((bt) => ({
        ...bt,
        categoryName: category.categoryName,
      })),
    )
  }

  const normalizedSearchTerm = searchTerm.trim().toLowerCase()

  // Get all matching business types with relevance scoring
  const matchedBusinessTypes = mccCategories.flatMap((category) =>
    category.businessTypes
      .map((bt) => {
        const businessTypeName = bt.name.toLowerCase()
        const categoryName = category.categoryName.toLowerCase()
        const mccString = bt.mcc.toString()

        let relevanceScore = 0
        let matches = false

        // Highest priority: Category name matches
        if (categoryName.includes(normalizedSearchTerm)) {
          if (categoryName === normalizedSearchTerm) {
            relevanceScore += 1000
          } else if (categoryName.startsWith(normalizedSearchTerm)) {
            relevanceScore += 900
          } else if (
            categoryName
              .split(' ')
              .some((word) => word.startsWith(normalizedSearchTerm))
          ) {
            relevanceScore += 800
          } else {
            relevanceScore += 700
          }
          matches = true
        }

        // Medium priority: Business type name matches
        if (businessTypeName.includes(normalizedSearchTerm)) {
          if (businessTypeName === normalizedSearchTerm) {
            relevanceScore += 600
          } else if (businessTypeName.startsWith(normalizedSearchTerm)) {
            relevanceScore += 500
          } else if (
            businessTypeName
              .split(' ')
              .some((word) => word.startsWith(normalizedSearchTerm))
          ) {
            relevanceScore += 400
          } else {
            relevanceScore += 300
          }
          matches = true
        }

        // Low priority: MCC code matches
        if (mccString.includes(normalizedSearchTerm)) {
          if (mccString === normalizedSearchTerm) {
            relevanceScore += 200
          } else if (mccString.startsWith(normalizedSearchTerm)) {
            relevanceScore += 100
          }
          matches = true
        }

        return matches
          ? {
              ...bt,
              categoryName: category.categoryName,
              relevanceScore,
            }
          : null
      })
      .filter(
        (bt): bt is BusinessTypeWithCategory & { relevanceScore: number } =>
          bt !== null,
      ),
  )

  // Sort by relevance score (highest first)
  return matchedBusinessTypes.sort(
    (a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0),
  )
}
